import Swal from 'sweetalert2';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';
import BranchGroupList from '../../Shared/BranchGroupList.vue';
import CollapsibleSummary from '../../Shared/CollapsibleSummary.vue';
import { buildDispenseAuthorizationHtml, openPrintWindow } from './printDispenseAuthorization.js';

const STATUS_FILTER_OPTIONS = [
    { value: 0, label: 'كل الحالات' },
    { value: 10, label: 'مسودة' },
    { value: 1, label: 'قيد المراجعة' },
    { value: 2, label: 'معتمد' },
    { value: 3, label: 'مرفوض' },
    { value: 4, label: 'مطلوب تعديل' },
    { value: 5, label: 'تم التحويل للصرف' },
];

const STATUS_LABELS = {
    0: 'مسودة',
    1: 'قيد مراجعة الصيدلة',
    2: 'معتمد',
    3: 'مرفوض',
    4: 'مطلوب تعديل',
    5: 'تم التحويل للصرف',
};

const STATUS_BADGE_CLASSES = {
    0: 'bg-label-secondary',
    1: 'bg-label-info',
    2: 'bg-label-success',
    3: 'bg-label-danger',
    4: 'bg-label-warning',
    5: 'bg-label-primary',
};

export default {
    name: 'MedicineOrders',
    mixins: [HelperMixin],
    components: {
        PaginationHelper,
        BranchGroupList,
        CollapsibleSummary,
    },

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewMedicineOrdersUi()) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية للوصول إلى طلبات المرافق.');
            this.href('/Home');
            return;
        }

        if (this.canCreateMedicineOrdersUi()) {
            this.loadFacilityProducts();
        }

        if (this.canConvertMedicineOrderToDispatchUi()) {
            this.loadCentralStockings();
        }

        // إدارة الصيدلة تبدأ بقائمة المستشفيات (طلب مهندس الوزارة 2026-10-05)؛
        // المرفق يرى طلباته كما كان.
        if (this.isMinistryView) {
            const q = this.$route.query || {};
            const branchId = Number(q.branchId);
            if (branchId) {
                this.viewMode = 'hospital';
                this.selectedBranch = { id: branchId, name: q.branchName || '' };
            } else {
                this.viewMode = q.view === 'all' ? 'all' : 'hospitals';
            }
        }
        await this.refreshAll();
    },

    computed: {
        statusFilterOptions() {
            return STATUS_FILTER_OPTIONS;
        },
        /** الوزارة (إدارة الصيدلة ومسؤول النظام) — لا المرفق. */
        isMinistryView() {
            return !!this.loginDetails && !this.isFacilityCenterRole();
        },
        summaryTitle() {
            if (this.viewMode === 'hospital' && this.selectedBranch) return 'ملخص — ' + (this.selectedBranch.name || 'المستشفى');
            return this.viewMode === 'all' ? 'ملخص — كل الطلبات' : 'ملخص — كل المستشفيات';
        },
        summaryHint() {
            const s = this.statistics || {};
            const parts = [(s.total || 0) + ' طلب'];
            if (s.urgentPending) parts.push('عاجل معلّق: ' + s.urgentPending);
            if (s.lastOn) parts.push('آخر طلب: ' + this.$options.filters.moment(s.lastOn));
            return parts.join(' · ');
        },
        branchGroupRows() {
            return (this.branchRows || []).map((r) => ({
                key: r.branchesId,
                id: r.branchesId,
                name: r.branchName || '—',
                total: r.total || 0,
                lastDate: r.lastOn,
                badges: [
                    { label: 'عاجل معلّق', value: r.urgentPending, cls: 'bg-danger' },
                    { label: 'قيد المراجعة', value: r.pending, cls: 'bg-label-warning' },
                    { label: 'مطلوب تعديل', value: r.modification, cls: 'bg-label-info' },
                    { label: 'معتمدة', value: r.approved, cls: 'bg-label-success' },
                    { label: 'مصروفة', value: r.converted, cls: 'bg-label-primary' },
                    { label: 'مرفوضة', value: r.rejected, cls: 'bg-label-secondary' },
                ],
            }));
        },
        statsCards() {
            const s = this.statistics || {};
            return [
                this.statCard('قيد المراجعة', s.pending, 'warning', 'ti ti-clock', 'بانتظار مراجعة الصيدلة'),
                this.statCard('معتمدة', s.approved, 'success', 'ti ti-circle-check', 'جاهزة للتحويل إلى صرف'),
                this.statCard('مرفوضة', s.rejected, 'danger', 'ti ti-circle-x', 'لن تُعالج'),
                this.statCard('مطلوب تعديل', s.modification, 'info', 'ti ti-edit', 'بانتظار تعديل من المرفق'),
            ];
        },
        canCreateOrder() {
            return this.canCreateMedicineOrdersUi();
        },
        canConvertToDispatch() {
            return this.canConvertMedicineOrderToDispatchUi();
        },
        canReview() {
            return this.canReviewMedicineOrdersUi();
        },
        isMinistryAdmin() {
            return this.hasInventoryAndStockAccessUi();
        },
        isFacilityUser() {
            return this.canCreateOrder;
        },
        hasRemainingToDispense() {
            if (!this.detail || !this.detail.lines) return false;
            return this.detail.lines.some((l) => {
                const approved = l.quantityApproved != null ? Number(l.quantityApproved) : 0;
                const done = Number(l.quantityDispensed || 0);
                return approved - done > 0;
            });
        },
        dispenseSelectedTotalQty() {
            if (!this.dispenseLines || !this.dispenseLines.length) return 0;
            return this.dispenseLines
                .filter((x) => x.selected && x.qtyToDispense > 0)
                .reduce((s, x) => s + Number(x.qtyToDispense || 0), 0);
        },
        createTotalQty() {
            const lines = this.createForm.lines || [];
            return lines
                .filter((l) => l.productsId)
                .reduce((s, l) => s + Number(this.effectiveLineQuantity(l) || 0), 0);
        },
        /** «حد الطلب» يخص طلبات الصرف فقط — الإرجاع والتالف لا معنى للحد فيها. */
        requiresReorderLevel() {
            return Number(this.createForm.operationType || 1) === 1;
        },
    },

    data() {
        return {
            /* الأقسام المطوية في نافذة التفاصيل.
               تبدأ مطوية عمداً: المطلوب «معلومة عامة مختصرة» تُقرأ بنظرة،
               ومن أراد التفصيل فتحه — فالمراجع يمرّ على عشرات الطلبات لا على طلب. */
            openDetailSections: [],

            // ── العرض حسب المستشفى (للوزارة) ──
            // hospitals: قائمة المستشفيات · hospital: طلبات مستشفى واحد · all: القائمة الكاملة كما كانت
            viewMode: 'all',
            selectedBranch: null,
            branchRows: [],
            branchLoading: false,
            // الأسطر المفتوحة في الجدول: { [orderId]: { loading, detail } }
            openRows: {},

            // وضع الطلب المبدئي بعدد الحالات (طلب العميل 2026-09-23)
            caseBasedMode: false,
            // Filters & pagination
            filters: {
                pageNo: 1,
                pageSize: 25,
                search: '',
                status: 0,
                from: null,
                to: null,
            },
            pages: 0,
            Info: [],
            statistics: {},
            loadingList: false,

            // Reference lists
            facilityProductOptions: [],
            centralStockingOptions: [],

            // Detail dialog
            detail: null,
            detailDialog: false,
            detailLoading: false,

            // Create / edit dialog
            createDialog: false,
            editingOrderId: null,
            editingFromModification: false,
            createForm: {
                operationType: 1,
                requestType: 1,
                urgentReason: '',
                note: '',
                lines: [{ productsId: null, quantity: 1, lineNote: '', reorderLevel: null }],
            },
            // سقف أصناف الطلب العاجل — يُعرض إرشادياً، والفرض النهائي على الخادم.
            urgentMaxItems: 2,

            // Review dialog
            reviewDialog: false,
            reviewForm: {
                id: 0,
                action: 'Approve',
                reviewNote: '',
                lines: [],
            },

            // Convert to dispatch dialog
            convertDialog: false,
            convertForm: {
                id: 0,
                sourceStockingId: null,
                note: '',
            },

            // Legacy dispense (kept for admin to use existing flow)
            dispenseDialog: false,
            dispenseLines: [],
            dispenseHeaderNote: '',
            dispenseSourceStockingId: null,
        };
    },

    filters: {
        moment(date) {
            if (!date) return '—';
            try {
                const d = new Date(date);
                if (isNaN(d.getTime())) return '—';
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                return `${dd}/${mm}/${d.getFullYear()}`;
            } catch (e) {
                return '—';
            }
        },
    },

    methods: {
        toggleSection(key) {
            const i = this.openDetailSections.indexOf(key);
            if (i >= 0) this.openDetailSections.splice(i, 1);
            else this.openDetailSections.push(key);
        },

        isSectionOpen(key) {
            return this.openDetailSections.indexOf(key) >= 0;
        },

        // ─── Lookups ─────────────────────────────────────────────
        loadFacilityProducts() {
            this.$http.GetFacilityProductsForOrders()
                .then((r) => {
                    this.facilityProductOptions = (r && r.data && r.data.info) || [];
                    // المسودات القديمة (قبل الخاصية) تُفتح بلا حد — نعبّئه من الحد المحفوظ للمرفق إن وُجد
                    this.prefillMissingReorderLevels();
                })
                .catch(() => {
                    this.facilityProductOptions = [];
                });
        },

        loadCentralStockings() {
            this.$http.GetAllStocking()
                .then((r) => {
                    const all = (r && r.data && r.data.info) || [];
                    this.centralStockingOptions = all.filter((s) => !s.branchesId);
                })
                .catch(() => {
                    this.centralStockingOptions = [];
                });
        },

        // ─── Filters & data load ─────────────────────────────────
        ApplyFilters() {
            this.refreshAll();
        },

        refreshAll() {
            const list = this.viewMode === 'hospitals' ? this.loadBranchGroups() : this.GetInfo(1);
            return Promise.all([list, this.GetStatistics()]);
        },

        currentBranchId() {
            return this.viewMode === 'hospital' && this.selectedBranch ? this.selectedBranch.id : null;
        },

        loadBranchGroups() {
            this.branchLoading = true;
            return this.$http.GetMedicineOrdersByBranch(this.filters.from, this.filters.to, this.filters.status)
                .then((r) => { this.branchRows = (r && r.data && r.data.info) || []; })
                .catch(() => { this.branchRows = []; })
                .finally(() => { this.branchLoading = false; });
        },

        openBranch(row) {
            this.selectedBranch = { id: row.id, name: row.name };
            this.switchView('hospital');
        },

        switchView(mode) {
            this.viewMode = mode;
            if (mode !== 'hospital') this.selectedBranch = null;
            this.openRows = {};
            const query = {};
            if (mode === 'hospital' && this.selectedBranch) {
                query.branchId = String(this.selectedBranch.id);
                query.branchName = this.selectedBranch.name;
            } else if (mode === 'all') {
                query.view = 'all';
            }
            this.$router.replace({ path: this.$route.path, query }).catch(() => { });
            this.refreshAll();
        },

        // ── السطر المختصر يُفتح على أصنافه دون مغادرة القائمة ──
        isRowOpen(item) {
            return !!(item && this.openRows[item.id]);
        },
        toggleRow(item) {
            if (!item) return;
            if (this.openRows[item.id]) {
                this.$delete(this.openRows, item.id);
                return;
            }
            this.$set(this.openRows, item.id, { loading: true, detail: null });
            this.$http.GetMedicineOrder(item.id)
                .then((r) => {
                    if (!this.openRows[item.id]) return;
                    this.$set(this.openRows, item.id, { loading: false, detail: (r && r.data && r.data.info) || null });
                })
                .catch(() => {
                    if (!this.openRows[item.id]) return;
                    this.$set(this.openRows, item.id, { loading: false, detail: null });
                });
        },

        ResetFilters() {
            this.filters.search = '';
            this.filters.status = 0;
            this.filters.from = null;
            this.filters.to = null;
            this.filters.pageSize = 25;
            this.ApplyFilters();
        },

        GetInfo(pageNo) {
            this.filters.pageNo = pageNo || 1;
            this.loadingList = true;
            this.$blockUI.Start();
            this.$http
                .GetMedicineOrders(
                    this.filters.pageNo,
                    this.filters.pageSize,
                    this.filters.search,
                    this.filters.status,
                    this.filters.from,
                    this.filters.to,
                    this.currentBranchId(),
                )
                .then((res) => {
                    this.$blockUI.Stop();
                    this.loadingList = false;
                    this.Info = (res && res.data && res.data.info) || [];
                    this.pages = (res && res.data && res.data.count) || 0;
                })
                .catch(() => {
                    this.$blockUI.Stop();
                    this.loadingList = false;
                    this.Info = [];
                    this.pages = 0;
                });
        },

        GetStatistics() {
            this.$http
                .GetMedicineOrdersStatistics(this.filters.from, this.filters.to, this.currentBranchId())
                .then((r) => {
                    this.statistics = (r && r.data) || {};
                })
                .catch(() => {
                    this.statistics = {};
                });
        },

        // ─── Status / labels ─────────────────────────────────────
        tableRowNumber(index) {
            const pn = Number(this.filters.pageNo || 1);
            const ps = Number(this.filters.pageSize || 25);
            return (pn - 1) * ps + index + 1;
        },

        getUiStatus(item) {
            if (!item) return 0;
            if (item.uiStatus != null) return Number(item.uiStatus);
            const raw = Number(item.status || 0);
            if (raw === 4 && item.dispensedOn) return 5;
            return raw;
        },

        statusLabel(item) {
            const ui = typeof item === 'object' ? this.getUiStatus(item) : Number(item || 0);
            return STATUS_LABELS[ui] || '—';
        },

        statusBadgeClass(item) {
            const ui = typeof item === 'object' ? this.getUiStatus(item) : Number(item || 0);
            return STATUS_BADGE_CLASSES[ui] || 'bg-label-secondary';
        },

        operationTypeLabel(t) {
            const n = Number(t);
            if (n === 2) return 'إرجاع أصناف';
            if (n === 3) return 'تالف';
            if (n === 4) return 'صلاحية';
            return 'صرف دواء';
        },

        operationTypeBadgeClass(t) {
            const n = Number(t);
            if (n === 2) return 'bg-label-warning';
            if (n === 3) return 'bg-label-danger';
            if (n === 4) return 'bg-label-info';
            return 'bg-label-primary';
        },

        // ─── Action gating helpers ───────────────────────────────
        rowCanEdit(item) {
            if (!this.isFacilityUser || !item) return false;
            const ui = this.getUiStatus(item);
            return ui === 0 || ui === 4;
        },

        rowCanSubmit(item) {
            return this.rowCanEdit(item);
        },

        rowCanCancel(item) {
            return this.rowCanEdit(item);
        },

        rowCanReview(item) {
            return this.canReview && item && this.getUiStatus(item) === 1;
        },

        rowCanConvert(item) {
            return this.canConvertToDispatch && item && this.getUiStatus(item) === 2 && !item.dispatchId;
        },

        // ─── Create / Edit ───────────────────────────────────────
        openCreateOrder() {
            this.editingOrderId = null;
            this.editingFromModification = false;
            this.createForm = {
                operationType: 1,
                requestType: 1,
                urgentReason: '',
                note: '',
                lines: [{ productsId: null, quantity: 1, lineNote: '', reorderLevel: null }],
            };
            if (!this.facilityProductOptions.length) this.loadFacilityProducts();
            this.createDialog = true;
        },

        /**
         * الكمية المحسوبة من عدد الحالات — نفس معادلة الخادم بالضبط
         * (حالات × جرعة × أيام، مقرَّبة لأعلى؛ نصف علبة لا يُصرف).
         * تُرجع null إن لم تكتمل المدخلات، فلا يُعرض رقم بلا سند.
         */
        caseBasedQuantity(line) {
            if (!line) return null;
            const cases = Number(line.caseCount || 0);
            const dose = Number(line.dosePerCase || 0);
            if (cases <= 0 || dose <= 0) return null;
            const days = Number(line.treatmentDays || 0) > 0 ? Number(line.treatmentDays) : 1;
            const total = cases * dose * days;
            if (total <= 0) return null;
            return Math.ceil(total);
        },

        /** الكمية المعتمدة للسطر: المحسوبة تَجُبّ المُدخَلة في وضع الحالات. */
        effectiveLineQuantity(line) {
            if (this.caseBasedMode) {
                const q = this.caseBasedQuantity(line);
                if (q != null) return q;
            }
            return Number(line.quantity || 0);
        },

        /** سند الكمية المبدئية كما يُعرض في التفاصيل. */
        caseBasisText(ln) {
            if (!ln || !ln.isCaseBasedEstimate) return '';
            const parts = [];
            if (ln.caseCount) parts.push(`${ln.caseCount} حالة`);
            if (ln.dosePerCase) parts.push(`جرعة ${ln.dosePerCase}`);
            if (ln.treatmentDays) parts.push(`${ln.treatmentDays} يوم`);
            return parts.join(' × ');
        },

        addCreateLine() {
            this.createForm.lines.push({
                productsId: null, quantity: 1, lineNote: '', reorderLevel: null,
                caseCount: null, dosePerCase: null, treatmentDays: null,
            });
        },

        removeCreateLine(index) {
            if (this.createForm.lines.length > 1) {
                this.createForm.lines.splice(index, 1);
            }
        },

        closeCreateDialog() {
            this.createDialog = false;
            this.editingOrderId = null;
            this.editingFromModification = false;
        },

        openEditOrder(item) {
            const targetId = item && item.id;
            if (!targetId) return;

            this.$blockUI.Start();
            this.$http.GetMedicineOrder(targetId)
                .then((r) => {
                    this.$blockUI.Stop();
                    const info = (r && r.data && r.data.info) || null;
                    if (!info) return;
                    this.editingOrderId = info.id;
                    this.editingFromModification = info.uiStatus === 4;
                    const lines = (info.lines && info.lines.length)
                        ? info.lines.map((l) => ({
                            productsId: l.productsId,
                            quantity: l.quantityRequested,
                            lineNote: '',
                            reorderLevel: l.reorderLevel != null ? Number(l.reorderLevel) : null,
                        }))
                        : [{ productsId: null, quantity: 1, lineNote: '', reorderLevel: null }];
                    this.createForm = {
                        operationType: info.operationType != null ? info.operationType : 1,
                        requestType: info.requestType != null ? info.requestType : 1,
                        urgentReason: info.urgentReason || '',
                        note: info.note || '',
                        lines,
                    };
                    if (!this.facilityProductOptions.length) this.loadFacilityProducts();
                    else this.prefillMissingReorderLevels();
                    this.detailDialog = false;
                    this.createDialog = true;
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'تعذر تحميل بيانات الطلب.';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },

        buildCreatePayload() {
            const lines = (this.createForm.lines || [])
                .filter((l) => l.productsId && Number(this.effectiveLineQuantity(l)) > 0)
                .map((l) => ({
                    productsId: Number(l.productsId),
                    quantity: Number(this.effectiveLineQuantity(l)),
                    lineNote: l.lineNote || null,
                    reorderLevel: (l.reorderLevel === null || l.reorderLevel === undefined)
                        ? null
                        : Number(l.reorderLevel),
                    // تُرسل المدخلات فقط في وضع الحالات — والخادم يعيد الحساب ولا يثق بالكمية.
                    caseCount: this.caseBasedMode && l.caseCount ? Number(l.caseCount) : null,
                    dosePerCase: this.caseBasedMode && l.dosePerCase ? Number(l.dosePerCase) : null,
                    treatmentDays: this.caseBasedMode && l.treatmentDays ? Number(l.treatmentDays) : null,
                }));

            return {
                operationType: this.createForm.operationType || 1,
                requestType: this.createForm.requestType || 1,
                urgentReason: this.createForm.requestType === 2 ? (this.createForm.urgentReason || null) : null,
                note: this.createForm.note || null,
                lines,
            };
        },

        /** الوحدة الأساسية للصنف المختار — تُعرض تحت خانة حد الطلب منعاً للالتباس. */
        lineUnitLabel(line) {
            if (!line || !line.productsId) return '';
            const p = (this.facilityProductOptions || []).find((x) => x.id === line.productsId);
            return p && p.baseUnitName ? `بوحدة: ${p.baseUnitName}` : '';
        },

        /** تعبئة حدود الطلب الفارغة من الحدود المحفوظة للمرفق (مسودات قديمة أو إعادة إرسال). */
        prefillMissingReorderLevels() {
            const lines = (this.createForm && this.createForm.lines) || [];
            lines.forEach((line) => this.onCreateLineProductChange(line));
        },

        /** عند اختيار صنف: تعبئة حد الطلب بالقيمة المحفوظة للمرفق إن وُجدت. */
        onCreateLineProductChange(line) {
            if (!line || !line.productsId) return;
            if (line.reorderLevel !== null && line.reorderLevel !== undefined) return;
            const p = (this.facilityProductOptions || []).find((x) => x.id === line.productsId);
            if (p && p.reorderLevel !== null && p.reorderLevel !== undefined) {
                line.reorderLevel = Number(p.reorderLevel);
            }
        },

        /// تحقق محلي قبل الإرسال — الفرض النهائي على الخادم دائماً.
        validateReorderLevelsBeforeSubmit() {
            if (!this.requiresReorderLevel) return null;
            const missing = (this.createForm.lines || [])
                .filter((l) => l.productsId && Number(l.quantity) > 0)
                .some((l) => l.reorderLevel === null || l.reorderLevel === undefined || Number(l.reorderLevel) < 0);
            return missing
                ? 'حد الطلب إلزامي لكل صنف — أدخل الرصيد الحرج الذي تريد التنبيه عنده قبل الإرسال.'
                : null;
        },

        /// تحقق محلي قبل الإرسال — الفرض النهائي على الخادم دائماً.
        validateUrgentBeforeSubmit() {
            if (this.createForm.requestType !== 2) return null;
            if (!String(this.createForm.urgentReason || '').trim())
                return 'سبب الطلب العاجل إلزامي — اكتب سبب الاستعجال قبل الإرسال.';
            const count = (this.createForm.lines || []).filter((l) => l.productsId && Number(l.quantity) > 0).length;
            if (count > this.urgentMaxItems)
                return `الطلب العاجل يقتصر على ${this.urgentMaxItems} صنف كحد أقصى (لديك ${count}).`;
            return null;
        },

        saveDraftFromDialog() {
            this.submitCreateOrUpdate(true);
        },

        submitFromDialog() {
            this.submitCreateOrUpdate(false);
        },

        submitCreateOrUpdate(saveOnly) {
            const payload = this.buildCreatePayload();

            if (!saveOnly && payload.lines.length === 0) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أضف صنفاً واحداً على الأقل بكمية صحيحة.');
                return;
            }

            if (!saveOnly) {
                const urgentError = this.validateUrgentBeforeSubmit();
                if (urgentError) {
                    this.$helper.ShowMessage('warning', 'تنبيه', urgentError);
                    return;
                }
                const reorderError = this.validateReorderLevelsBeforeSubmit();
                if (reorderError) {
                    this.$helper.ShowMessage('warning', 'تنبيه', reorderError);
                    return;
                }
            }

            this.$blockUI.Start();

            if (this.editingOrderId) {
                this.$http.UpdateFacilityOrder(this.editingOrderId, payload)
                    .then(() => {
                        if (saveOnly) {
                            this.$blockUI.Stop();
                            this.$helper.ShowMessage('success', 'تم', 'تم حفظ التعديلات.');
                            this.closeCreateDialog();
                            this.ApplyFilters();
                            return;
                        }
                        return this.$http.SubmitFacilityOrder(this.editingOrderId)
                            .then((res2) => {
                                this.$blockUI.Stop();
                                const msg = (res2 && res2.data) || 'تم إرسال الطلب إلى مراجعة الصيدلة.';
                                this.$helper.ShowMessage('success', 'تم', msg);
                                this.closeCreateDialog();
                                this.ApplyFilters();
                            });
                    })
                    .catch((err) => {
                        this.$blockUI.Stop();
                        const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                        this.$helper.ShowMessage('error', 'خطأ', msg);
                    });
                return;
            }

            this.$http.CreateFacilityOrder(payload, saveOnly)
                .then((res) => {
                    this.$blockUI.Stop();
                    const data = (res && res.data) || {};
                    const msg = data.message || 'تم.';
                    this.$helper.ShowMessage('success', 'تم', msg);
                    this.closeCreateDialog();
                    this.ApplyFilters();
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },

        // ─── Submit / Cancel (list-level) ────────────────────────
        submitOrderFromList(item) {
            if (!item || !this.rowCanSubmit(item)) return;
            const orderId = item.id;
            this.$blockUI.Start();
            this.$http.SubmitFacilityOrder(orderId)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('success', 'تم', (res && res.data) || 'تم إرسال الطلب.');
                    if (this.detail && this.detail.id === orderId) {
                        this.reloadDetail();
                    }
                    this.ApplyFilters();
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },

        cancelOrderFromList(item) {
            if (!item || !this.rowCanCancel(item)) return;
            const orderId = item.id;
            const code = item.code || '';
            const confirm = window.confirm(`هل تريد إلغاء/حذف الطلب ${code}؟`);
            if (!confirm) return;
            this.$blockUI.Start();
            this.$http.CancelFacilityOrder(orderId, '')
                .then((res) => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('success', 'تم', (res && res.data) || 'تم إلغاء الطلب.');
                    if (this.detail && this.detail.id === orderId) {
                        this.detailDialog = false;
                        this.detail = null;
                    }
                    this.ApplyFilters();
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },

        /// إغلاق إداري من إدارة الصيدلة لطلب عالق — يحرر دورة المرفق.
        forceCloseOrder(order) {
            if (!order || !order.canForceClose) return;
            const reason = window.prompt(`سبب الإغلاق الإداري للطلب ${order.code} (إلزامي):`);
            if (reason === null) return;
            if (!String(reason).trim()) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'سبب الإغلاق الإداري إلزامي.');
                return;
            }
            this.$blockUI.Start();
            this.$http.ForceCloseMedicineOrder(order.id, String(reason).trim())
                .then((res) => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('success', 'تم', (res && res.data) || 'تم الإغلاق الإداري.');
                    this.detailDialog = false;
                    this.detail = null;
                    this.ApplyFilters();
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },

        // ─── Detail view ─────────────────────────────────────────
        openDetail(item) {
            if (!item || !item.id) return;
            // كل فتح يبدأ من العرض المختصر — لا يرث طيّ طلبٍ سابق.
            this.openDetailSections = [];
            this.detailDialog = true;
            this.detailLoading = true;
            this.detail = null;
            this.$http.GetMedicineOrder(item.id)
                .then((r) => {
                    this.detailLoading = false;
                    this.detail = (r && r.data && r.data.info) || null;
                })
                .catch((err) => {
                    this.detailLoading = false;
                    const msg = err.response && err.response.data ? err.response.data : 'تعذر تحميل التفاصيل.';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },

        reloadDetail() {
            if (!this.detail || !this.detail.id) return Promise.resolve();
            return this.$http.GetMedicineOrder(this.detail.id)
                .then((r) => {
                    this.detail = (r && r.data && r.data.info) || null;
                })
                .catch(() => { });
        },

        closeDetail() {
            this.detailDialog = false;
            this.detail = null;
        },

        // ─── Review (pharmacy) ───────────────────────────────────
        openReview() {
            if (!this.detail) return;
            this.reviewForm = {
                id: this.detail.id,
                action: 'Approve',
                reviewNote: '',
                lines: (this.detail.lines || []).map((l) => ({
                    lineId: l.id,
                    productName: l.productName,
                    quantityRequested: l.quantityRequested,
                    quantityApproved: l.quantityApproved != null ? Number(l.quantityApproved) : l.quantityRequested,
                    // من واجهة «بيانات الاحتياج» — مرجع المراجع للتعديل بالزيادة أو النقصان
                    monthlyNeed: l.monthlyNeed != null ? Number(l.monthlyNeed) : null,
                    avgMonthlyConsumption: l.avgMonthlyConsumption != null ? Number(l.avgMonthlyConsumption) : null,
                })),
            };
            this.reviewDialog = true;
        },

        submitReview() {
            if (!this.reviewForm.id) return;

            if (this.reviewForm.action === 'Reject' && !(this.reviewForm.reviewNote || '').trim()) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أضف ملاحظة سبب الرفض.');
                return;
            }
            if (this.reviewForm.action === 'RequestModification' && !(this.reviewForm.reviewNote || '').trim()) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'أضف ملاحظة بمتطلبات التعديل.');
                return;
            }

            const body = {
                id: this.reviewForm.id,
                action: this.reviewForm.action,
                approve: this.reviewForm.action === 'Approve',
                reviewNote: this.reviewForm.reviewNote || null,
                lines: this.reviewForm.action === 'Approve'
                    ? this.reviewForm.lines.map((l) => ({
                        lineId: l.lineId,
                        quantityApproved: Number(l.quantityApproved || 0),
                    }))
                    : null,
            };

            this.$blockUI.Start();
            this.$http.ReviewMedicineOrder(body)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('success', 'تم', (res && res.data) || 'تمت المراجعة.');
                    this.reviewDialog = false;
                    if (this.detail && this.detail.id === this.reviewForm.id) {
                        this.reloadDetail();
                    }
                    this.ApplyFilters();
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },

        // ─── Convert to Dispatch ─────────────────────────────────
        openConvertDialog(item) {
            const target = item || this.detail;
            if (!target || !target.id) return;
            this.convertForm = {
                id: target.id,
                sourceStockingId: this.centralStockingOptions.length === 1
                    ? this.centralStockingOptions[0].id
                    : null,
                note: target.note || '',
            };
            if (!this.centralStockingOptions.length) this.loadCentralStockings();
            this.convertDialog = true;
        },

        confirmConvertToDispatch() {
            if (!this.convertForm.id) return;
            const body = {
                sourceStockingId: this.convertForm.sourceStockingId || null,
                note: this.convertForm.note || null,
            };
            this.$blockUI.Start();
            this.$http.ConvertFacilityOrderToDispatch(this.convertForm.id, body)
                .then((res) => {
                    this.$blockUI.Stop();
                    const data = (res && res.data) || {};
                    this.$helper.ShowMessage('success', 'تم', data.message || 'تم التحويل إلى عملية صرف.');
                    this.convertDialog = false;
                    if (this.detail && this.detail.id === this.convertForm.id) {
                        this.reloadDetail();
                    }
                    this.ApplyFilters();
                    if (data.dispatchId) {
                        Swal.fire({
                            title: 'فتح عملية الصرف',
                            text: 'هل تريد فتح عملية الصرف الجديدة الآن؟',
                            icon: 'question',
                            showCancelButton: true,
                            confirmButtonText: 'نعم، افتح الآن',
                            cancelButtonText: 'لاحقاً',
                        }).then((res) => {
                            if (res.isConfirmed) {
                                this.$router.push({ path: '/DispatchV3', query: { id: data.dispatchId } });
                            }
                        });
                    }
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },

        // ─── Print (legacy, kept for admins) ─────────────────────
        printDispenseAuthorization() {
            if (!this.detail || !this.detail.canPrintDispenseAuthorization) return;
            this.$http.MarkMedicineOrderPrinted(this.detail.id)
                .then(() => {
                    const path = this.logoPath || '/assets/img/1/logo.png';
                    const logoUrl = typeof window !== 'undefined' && window.location
                        ? `${window.location.origin}${path.startsWith('/') ? path : '/' + path}`
                        : '';
                    const html = buildDispenseAuthorizationHtml(this.detail, {
                        buildFor: this.BuildFor,
                        logoUrl,
                    });
                    if (!openPrintWindow(html)) {
                        this.$helper.ShowMessage('warning', 'تنبيه', 'اسمح بالنوافذ المنبثقة في المتصفح لطباعة المستند.');
                        return;
                    }
                    this.reloadDetail();
                })
                .catch((err) => {
                    const msg = err.response && err.response.data ? err.response.data : 'تعذر تسجيل الطباعة.';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },

        // ─── Receipt verification (legacy facility action) ───────
        openReceiptVerification() {
            if (!this.detail || !this.detail.id) return;
            this.$blockUI.Start();
            this.$http.CreateReceiptVerificationFromOrder(this.detail.id)
                .then((response) => {
                    this.$blockUI.Stop();
                    const data = response.data || {};
                    this.$helper.ShowMessage('success', 'تم', data.message || 'تم فتح محضر التحقق.');
                    if (data.id) {
                        this.$router.push({ path: '/ReceiptVerification', query: { id: data.id } });
                    }
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    const msg = err.response && err.response.data ? err.response.data : 'خطأ';
                    this.$helper.ShowMessage('error', 'خطأ', msg);
                });
        },
    },
};
